//#region Imports
import { useAtom } from 'jotai';
import CloseIcon from '@mui/icons-material/Close';
import { useCallback, useEffect, useState } from 'react';
import { faEmptySet } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from '@mui/material';

import MovementItem from './MovementItem';
import customAxios from '../../utils/customAxios';
import { GlobalVariables } from '../../globalVar';
import { getToast } from '../../utils/toastConfig';
import { movementsArrayAtom } from '../../stores/movementsStore';
//#endregion

const MultipleMovementsModal = ({ show, onHide, refreshData }) => {
  const [movements, setMovements] = useAtom(movementsArrayAtom);
  const [itemsDetails, setItemsDetails] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const fetchItemDetails = async (movement) => {
    const endpoint = `${GlobalVariables.getURL()}items/${movement.itemId}`;
    const response = await customAxios.get(endpoint, {
      headers: {
        Authorization: 'bearer ' + localStorage.getItem('token'),
      },
    });
    return response.data.data;
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const itemDetailsPromises = movements.map(fetchItemDetails);
      const data = await Promise.all(itemDetailsPromises);
      setItemsDetails(data);
    } catch (error) {
      setError(error);
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, [movements]);

  useEffect(() => {
    if (movements.length > 0) {
      fetchData();
    }
  }, [fetchData, movements]);

  const handleDeleteMovement = (index) => {
    setMovements((prev) => {
      if (prev && prev.length !== 0) {
        const newMovements = prev.filter((_, i) => i !== index);
        return newMovements;
      }
      return prev;
    });
  };

  const handleSubmitOrders = async () => {
    setIsSubmitting(true);
    try {
      const endpoint = `${GlobalVariables.getURL()}movements/createMultiple`;
      const response = await customAxios.post(endpoint, movements, {
        headers: {
          Authorization: 'bearer ' + localStorage.getItem('token'),
        },
      });

      if (response.status === 200) {
        refreshData();
        onHide();
        setMovements([]);
        getToast({ status: 'success', title: 'Orden realizada' });
      }
    } catch (error) {
      console.log(error);
      getToast({
        status: 'error',
        title: error.response.data.message,
        duration: 4,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog
      open={show}
      onClose={onHide}
      fullWidth={true}
      maxWidth={'md'}
      sx={{}}
    >
      <DialogTitle>
        <Typography className='inventory-movmodal-title'>
          Movimientos
        </Typography>
      </DialogTitle>
      <IconButton
        aria-label='close'
        onClick={onHide}
        sx={{
          position: 'absolute',
          right: 20,
          top: 20,
        }}
      >
        <CloseIcon />
      </IconButton>
      <DialogContent dividers>
        {loading ? (
          <Box display='flex' alignItems='center' justifyContent='center'>
            <CircularProgress color='primary' />
          </Box>
        ) : error ? (
          <p>Error loading data: {error.message}</p>
        ) : movements.length === 0 ? (
          <Box
            display='flex'
            alignItems='center'
            flexDirection='column'
            justifyContent='center'
            color='#b0b0b0'
          >
            <FontAwesomeIcon
              icon={faEmptySet}
              size='4x'
              style={{ marginBottom: '3%' }}
            />
            <Typography variant='h5'>No tienes movimientos aún</Typography>
          </Box>
        ) : (
          itemsDetails &&
          itemsDetails.map((itemDetail, index) => {
            if (movements[index]) {
              return (
                <MovementItem
                  key={index}
                  index={index}
                  handleDelete={handleDeleteMovement}
                  movementInfo={movements[index]}
                  item={itemDetail}
                />
              );
            }
            return <></>;
          })
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleSubmitOrders} disabled={isSubmitting}>
          {isSubmitting ? <CircularProgress size='2rem' /> : 'Confirmar orden'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MultipleMovementsModal;
